import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import { format } from 'date-fns';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../../../utils/axiosConfig';

const Bookings = ({ supplier }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchBookings();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [supplier._id, page, rowsPerPage]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/admin/supplier/${supplier._id}/bookings`, {
        params: {
          page: page + 1,
          limit: rowsPerPage
        }
      });
      setBookings(response.data);
    } catch (err) {
      setError('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewBooking = (booking) => {
    setSelectedBooking(booking);
    setDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading && page === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Booking ID</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bookings.map((booking) => (
                <TableRow key={booking._id}>
                  <TableCell>{booking._id.slice(-6)}</TableCell>
                  <TableCell>{booking.customer.name}</TableCell>
                  <TableCell>{format(new Date(booking.date), 'MMM d, yyyy')}</TableCell>
                  <TableCell>{format(new Date(booking.time), 'HH:mm')}</TableCell>
                  <TableCell>{booking.service.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={booking.status}
                      color={getStatusColor(booking.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleViewBooking(booking)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {bookings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No bookings found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={-1}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      {/* Booking Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedBooking && (
          <>
            <DialogTitle>
              Booking Details
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ py: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Booking ID
                </Typography>
                <Typography variant="body2" paragraph>
                  {selectedBooking._id}
                </Typography>

                <Typography variant="subtitle2" gutterBottom>
                  Customer Information
                </Typography>
                <Typography variant="body2" paragraph>
                  Name: {selectedBooking.customer.name}<br />
                  Email: {selectedBooking.customer.email}<br />
                  Phone: {selectedBooking.customer.phone}
                </Typography>

                <Typography variant="subtitle2" gutterBottom>
                  Service Details
                </Typography>
                <Typography variant="body2" paragraph>
                  Service: {selectedBooking.service.name}<br />
                  Duration: {selectedBooking.service.duration} minutes<br />
                  Price: ${selectedBooking.service.price}
                </Typography>

                <Typography variant="subtitle2" gutterBottom>
                  Booking Time
                </Typography>
                <Typography variant="body2" paragraph>
                  Date: {format(new Date(selectedBooking.date), 'MMMM d, yyyy')}<br />
                  Time: {format(new Date(selectedBooking.time), 'HH:mm')}
                </Typography>

                {selectedBooking.notes && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      Notes
                    </Typography>
                    <Typography variant="body2" paragraph>
                      {selectedBooking.notes}
                    </Typography>
                  </>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Bookings; 